import { useEffect, useState, useCallback } from "react";

export function useMenu() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [alertDialog, setAlertDialog] = useState(null);

  // Fetch items logic
  const fetchItems = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError(null);
      const url = new URL(`${process.env.REACT_APP_SERVER_PATH}/products`);
      if (searchTerm && searchTerm.trim().length > 0) {
        url.searchParams.append("keyword", searchTerm.trim());
      }
      url.searchParams.append("page", String(page));
      const response = await fetch(url.toString(), {
        signal,
      });

      console.log("Fetch URL:", url.toString(), response);
      if (!response.ok) throw new Error("Failed to fetch products");
      const data = await response.json();
      const mappedItems = (Array.isArray(data) ? data : []).map((item) => ({
        id: item._id,
        name: item.productName,
        brand: item.brandName,
        category: item.category,
        price: item.orignalPrice,
        discountPrice: item.discountPrice,
        image: item.images && item.images[0] ? item.images[0].Location : "",
        status: "active",
      }));
      console.log("Fetched items:", mappedItems);
      
      // If no items returned and we're not on page 1, go back to previous page
      if (mappedItems.length === 0 && page > 1) {
        setToast({
          message: "No more items to display. You've reached the last page.",
          type: "info",
        });
        setPage((prevPage) => prevPage - 1);
        setHasMore(false);
        return;
      }
      
      setItems(mappedItems);
      setTotalItems(mappedItems.length);
      
      // Determine if there are more pages (assuming page size is 15)
      setHasMore(mappedItems.length >= 15);
    } catch (err) {
      if (err.name !== "AbortError") {
        setError(err.message || "Unknown error");
      }
    } finally {
      setLoading(false);
    }
  }, [searchTerm, page]);

  // Used for auto reload on search/page change
  useEffect(() => {
    const controller = new AbortController();
    fetchItems(controller.signal);
    return () => controller.abort();
  }, [fetchItems]);

  // Manual reload function
  const reloadItems = () => {
    fetchItems();
  };

  const handleAddItem = (newItem) => {
    setItems((prevItems) => [...prevItems, newItem]);
    // Show success toast after adding
    setTimeout(() => {
      setToast({
        message: "Product added successfully!",
        type: "success",
      });
    }, 200);
  };
  const handleShowAddForm = () => setShowAddForm(true);
  const handleCloseAddForm = () => setShowAddForm(false);
  const handleEditItem = (id) => {
    const itemToEdit = items.find((item) => item.id === id);
    if (itemToEdit) {
      setEditingItem(itemToEdit);
      setShowEditForm(true);
    }
  };
  const handleUpdateItem = (updatedItem) => {
    setItems((prevItems) =>
      prevItems.map((item) => (item.id === updatedItem.id ? updatedItem : item))
    );
    // Show success toast after update
    setTimeout(() => {
      setToast({
        message: "Product updated successfully!",
        type: "success",
      });
    }, 200);
  };
  const handleCloseEditForm = () => {
    setShowEditForm(false);
    setEditingItem(null);
  };
  const handleDeleteItem = (id) => {
    // Show custom confirmation dialog
    setConfirmDialog({
      title: "Delete Product",
      message: "Are you sure you want to delete this product? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
      onConfirm: async () => {
        setConfirmDialog(null);
        
        try {
          const token = localStorage.getItem("token");
          
          if (!token) {
            setAlertDialog({
              title: "Authentication Required",
              message: "Please login again to continue.",
              type: "error",
            });
            return;
          }

          const response = await fetch(
            `${process.env.REACT_APP_SERVER_PATH}/products/${id}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.message || `Failed to delete product (${response.status})`
            );
          }

          // Successfully deleted - remove from local state
          setItems((prevItems) => prevItems.filter((item) => item.id !== id));
          setToast({
            message: "Product deleted successfully!",
            type: "success",
          });
        } catch (err) {
          console.error("Delete error:", err);
          setToast({
            message: err.message || "Failed to delete product. Please try again.",
            type: "error",
          });
        }
      },
      onCancel: () => {
        setConfirmDialog(null);
      },
    });
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const closeToast = () => setToast(null);

  const showAlert = (message, type = 'info', title = '') => {
    setAlertDialog({ message, type, title });
  };

  const closeAlert = () => setAlertDialog(null);

  return {
    showAddForm,
    showEditForm,
    editingItem,
    items,
    searchTerm,
    setSearchTerm,
    page,
    setPage,
    loading,
    error,
    hasMore,
    totalItems,
    toast,
    showToast,
    closeToast,
    confirmDialog,
    alertDialog,
    showAlert,
    closeAlert,
    handleAddItem,
    handleShowAddForm,
    handleCloseAddForm,
    handleEditItem,
    handleUpdateItem,
    handleCloseEditForm,
    handleDeleteItem,
    reloadItems,
  };
}
